import React, { forwardRef, type MouseEventHandler, PropsWithChildren } from 'react';
import type { RouterLocation } from '@funtech-int/rival';
import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';

import { RouterLink } from '@/shared/components/RouterLink/RouterLink';
import { mergeRefs } from '@/shared/lib/ref';

import styles from './_Layout.module.css';

interface LayoutProps {
    location: RouterLocation<any, any>;
    searchParams?: URLSearchParams;
    className?: string;
    withShadow?: boolean;
    dataTid?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    onShow?: VoidFunction;
    onMouseEnter?: MouseEventHandler<HTMLDivElement>;
    onMouseLeave?: MouseEventHandler<HTMLDivElement>;
    actions?: React.ReactNode;
}

export const Layout = forwardRef<HTMLDivElement, PropsWithChildren<LayoutProps>>(
    (
        {
            location,
            searchParams,
            className,
            dataTid,
            withShadow,
            onClick,
            onShow,
            onMouseEnter,
            onMouseLeave,
            children,
            actions,
        },
        ref
    ) => {
        const { ref: inViewRef } = useInView({
            onChange(inView: boolean) {
                if (inView) {
                    onShow?.();
                }
            },
            threshold: 0.8,
        });

        const refs = mergeRefs<HTMLDivElement>(ref, inViewRef);

        return (
            <div
                ref={refs}
                className={classnames(
                    styles.root,
                    {
                        [styles.rootWithShadow]: withShadow,
                    },
                    className
                )}
                data-tid={dataTid}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <figure className={styles.content}>{children}</figure>
                <RouterLink
                    to={location}
                    searchParams={searchParams}
                    className={styles.link}
                    onClick={onClick}
                    dataTid={dataTid ? `${dataTid}Link` : undefined}
                />
                {actions}
            </div>
        );
    }
);
